import React from "react";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Button,
  Switch,
  Paper,
  Divider,
  CircularProgress,
  TextField,
} from "@mui/material";
import { KeyboardArrowLeft } from "@mui/icons-material";
import {
  IconSuggestion,
  IconHelpCalendar,
  IconFolder,
  IconCommon,
  IconImage,
  IconPDFHelp,
  IconVideoHelp,
  IconDownloadHelp,
} from "Icons";
import { SupportButton } from "components";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import Avatar from '@mui/material/Avatar';

const theme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
    allVariants: {
      fontFamily: "Poppins, sans-serif",
    },
  },
});

const iconMap = {
  // Image files
  jpg: IconImage,
  jpeg: IconImage,
  png: IconImage,
  gif: IconImage,
  svg: IconImage,
  // Document files
  doc: IconCommon,
  docx: IconCommon,
  txt: IconCommon,
  rtf: IconCommon,
  // PDF
  pdf: IconPDFHelp,
  // Code files
  js: IconCommon,
  jsx: IconCommon,
  ts: IconCommon,
  tsx: IconCommon,
  html: IconCommon,
  css: IconCommon,
  py: IconCommon,
  java: IconCommon,
  // Video files
  mp4: IconVideoHelp,
  mov: IconVideoHelp,
  avi: IconVideoHelp,
  wmv: IconVideoHelp,
  // Audio files
  mp3: IconCommon,
  wav: IconCommon,
  ogg: IconCommon,
};

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${day} ${month}' ${year}`;
};

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#65C466",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const getFileIcon = (fileName) => {
  const extension = fileName.split(".").pop().toLowerCase();
  const IconComponent = iconMap[extension] || IconCommon;
  return <IconComponent style={{ width: "36px", height: "36px" }} />;
};

const TicketContent = ({ ticket, onClose, ticketId, ctx_api, ctx_user, onSupportSuccess }) => {
  const [text, setText] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [openNotes, setOpenNotes] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);

  const isDisabled = !ticket.attachment || ticket.attachment === "{}";
  const isAssigned = ticket.is_assigned === 1;

  useEffect(() => {
    if (ticket) {
      setIsCompleted(ticket.is_completed);
    }
  }, [ticket]);

  const handleSubmitNotes = async () => {
    const status = isCompleted ? "CLOSED" : "OPEN";
    try {
      const data = await ctx_api.privateRequest({
        cmd: "update_tickets_status",
        args: {
          ticketId: String(ticketId),
          status,
          notes: text,
        },
      });

      setOpenNotes(false);
      const rsp = data?.rsp[0]?.status;
      setIsCompleted(rsp === "CLOSED");
      onClose();
    } catch (error) {
      console.error("Failed to update ticket:", error);
    }
  };

  const handleToggle = async (event) => {
    const checked = event.target.checked;
    setIsCompleted(checked); // immediately toggle UI

    if (checked) setOpenNotes(true);
    else {
      try {
        await ctx_api.privateRequest({
          cmd: "update_tickets_status",
          args: {
            ticketId: String(ticketId),
            status: "OPEN",
            notes: "",
          },
        });
      } catch (err) {
        console.error(err);
        setIsCompleted(!checked); // revert on failure
      }
    }
  };

  const handleAttachmentPreview = () => {
    setOpenPreview(true);
  };

  const handleDownload = () => {
    if (!ticket.attachment) return;

    // Create a hidden link
    const link = document.createElement("a");
    link.href = ticket.attachment; // base64 data URL
    link.download = "attachment.png"; // default filename

    // Append, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getBase64FileSize = (base64String) => {
    if (!base64String) return 0;

    // Remove "data:image/...;base64," prefix if present
    const base64WithoutPrefix = base64String.split(",")[1];

    // Calculate padding
    const padding = (base64WithoutPrefix.match(/=+$/) || [""])[0].length;

    // Size in bytes
    const sizeInBytes = (base64WithoutPrefix.length * 3) / 4 - padding;

    return sizeInBytes;
  };


  const getStatusChip = () => (
    <Chip
      label={ticket.is_completed
        ? "COMPLETED"
        : ticket.is_assigned
          ? "TICKET ASSIGNED TO SUPPORT"
          : "IN PROGRESS"}
      size="medium"
      sx={{
        bgcolor: ticket.is_completed ? "#C3F5D4" : "#F5F5F5",
        fontWeight: "600",
        fontSize: "0.8rem",
        px: 1.5,
        py: 0.25,
      }}
    />
  );

  const getTypeText = () => {
    return ticket.ticket_type
      .replace("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <Box sx={{ p: 2, borderRadius: "10px" }}>
      <IconButton onClick={onClose} aria-label="back">
        <KeyboardArrowLeft />
      </IconButton>

      <Box sx={{ py: 4, px: 8 }}>
        {/* User Info Section */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "grey.300",
              mr: 2,
              display: "flex",
              overflow: "hidden",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Avatar
              src={ticket.member_image}
              alt="Profile"
              sx={{ width: "100%", height: "100%" }}
            >
              {/* Fallback pic if no image */}
              {ticket.member_name ? ticket.member_name[0].toUpperCase() : "?"}
            </Avatar>
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "start",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {ticket.member_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ticket.member_type===1 ? "Student": "Teacher"}
                </Typography>
              </Box>
              {getStatusChip()}
              <Chip
                label={getTypeText()}
                size="medium"
                sx={{
                  bgcolor: "#C3F5D4",
                  fontWeight: "600",
                  fontSize: "0.8rem",
                  px: 1.5,
                  py: 0.25,
                }}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            sx={{
              ml: "auto",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconHelpCalendar />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              {formatDate(new Date(ticket.created_on))}
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Divider
          sx={{ my: 2, borderColor: "#1A095D", borderBottomWidth: 1.5 }}
        />

        {/* Feature Request Content */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              alignContent: "center",
            }}
          >
            <IconSuggestion style={{ width: "24px", height: "24px" }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#2B2D42",
              }}
            >
              #{getTypeText()} - {ticket.ticket_id}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              mb: 2,
              color: "#2B2D42",
            }}
          >
            {ticket.suggestion_title
              ? `Feature name: ${ticket.suggestion_title}`
              : ticket.course_name
              ? `Course name: ${ticket.course_name}`
              : ticket.assessment_name
              ? `Assessment name: ${ticket.assessment_name}`
              : "Description"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#5F6368",
              fontSize: "0.75rem",
              maxHeight: "100px",
              overflowY: "auto",
            }}
          >
            {ticket.description}
          </Typography>
        </Box>

        {/* Attachments Section */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 2,
              fontSize: "0.8rem",
            }}
          >
            <IconFolder />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#2B2D42",
                fontSize: "1.125rem",
              }}
            >
              Attachments
            </Typography>
          </Box>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: "10px",
              border: "none",
              background: `
                linear-gradient(white, white) padding-box,
                linear-gradient(90deg, #3612C3 0%, #1A095D 100%) border-box
              `,
              borderWidth: "2px",
              borderStyle: "solid",
              borderColor: "transparent",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                opacity: isDisabled ? 0.5 : 1,
                pointerEvents: isDisabled ? "none" : "auto",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box>{getFileIcon("Screenshot1.jpg")}</Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "#2B2D42" }}
                >
                  {isDisabled ? "No attachments" :  ticket.attachment.name}
                </Typography>
                <Typography color="text.secondary">•</Typography>
                <Typography
                  sx={{
                    cursor: isDisabled ? "default" : "pointer",
                    background: isDisabled
                      ? "none"
                      : "linear-gradient(180deg, #3612C3 0%, #1A095D 100%)",
                    WebkitBackgroundClip: isDisabled ? "unset" : "text",
                    backgroundClip: isDisabled ? "unset" : "text",
                    color: isDisabled ? "gray" : "transparent",
                    display: "inline-block",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                  }}
                  onClick={() => {
                    if (!isDisabled) {
                      handleAttachmentPreview();
                    }
                  }}
                >
                  Preview
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  sx={{
                    cursor: isDisabled ? "default" : "pointer",
                    background: isDisabled
                      ? "none"
                      : "linear-gradient(180deg, #3612C3 0%, #1A095D 100%)",
                    WebkitBackgroundClip: isDisabled ? "unset" : "text",
                    backgroundClip: isDisabled ? "unset" : "text",
                    color: isDisabled ? "gray" : "transparent",
                    display: "inline-block",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                  }}
                >
                {isDisabled
                  ? "Size"
                  : `${(getBase64FileSize(ticket.attachment) / 1024).toFixed(2)} KB`}
                </Typography>
                <Button
                  variant="text"
                  endIcon={<IconDownloadHelp />}
                  onClick={handleDownload}
                  sx={{
                    color: isDisabled ? "gray" : "#2B2D42",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    textTransform: "none",
                    cursor: isDisabled ? "default" : "pointer",
                  }}
                >
                  Download
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: "5F6368",
                fontSize: "0.875rem",
              }}
            >
              {ticket.is_assigned ? "Assigned to Dev Support": "Assign this ticket to Dev Support"}
            </Typography>
            <SupportButton ticket={ticket} uMemberId={ctx_user.user.uid} disabled={isCompleted || isAssigned} onSuccess={onSupportSuccess}/>
          </Box>
          {ticket.notes && (
            <Box 
              sx={{ 
                border: '1px solid', 
                borderColor: 'grey.300', 
                borderRadius: 1, 
                p: 2, 
                bgcolor: 'grey.50',
                maxHeight: 80, 
                overflowY: 'auto'
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Remarks:
              </Typography>
              <Typography variant="body2">
                {ticket.notes}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: "5F6368",
                fontSize: "0.875rem",
              }}
            >
              Close the ticket
            </Typography>
            <IOSSwitch
              checked={isCompleted}
              onChange={handleToggle}
              inputProps={{ "aria-label": "completion toggle" }}
            />
            <Dialog
              open={openNotes}
              onClose={() => setOpenNotes(false)}
              maxWidth="sm"
              fullWidth
            >
              <Box sx={{ p: 3, br: 10 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Add Notes Before Closing Ticket
                </Typography>
                <TextField
                  label="Notes"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  multiline
                  rows={4}
                  fullWidth
                  margin="normal"
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Button onClick={() => setOpenNotes(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitNotes}
                    disabled={!text.trim()}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </Dialog>
            <Box
              display="flex"
              sx={{
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconHelpCalendar />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.875rem" }}
              >
                {formatDate(new Date())}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Attachment Preview</DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          {ticket.attachment && (
            <img
              src={ticket.attachment}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const ViewDetailsModal = ({ open, onClose, ticketId, ctx_api, ctx_user }) => {

  const [ticket, setTicket] = useState(null);

  const getAdminTicketsDetail = async () => {    
    try {
      const data = await ctx_api.privateRequest({
        cmd: "get_admin_tickets_detail",
        args: { 
	        ticketId: String(ticketId)
         },
      });

      const ticket = data?.rsp[0]?.details.details || [];
      setTicket(ticket);
    } catch (error) {}
  };
  
    const handleSupportSuccess = () => {
    getAdminTicketsDetail();
  };
  
  useEffect(() => {
    if (ticketId) getAdminTicketsDetail();
  }, [ticketId]);

  useEffect(() => {
    if (!open) {
      setTicket(null); // Clear ticket state when modal closes
    }
  }, [open]);

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "25px",
            overflow: "hidden",
          },
        }}
      >
        {!ticket ? (
          <Box
            sx={{
              py: 4,
              px: 8,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={20} />
            <Typography>Loading ticket details...</Typography>
          </Box>
        ) : (
          <TicketContent ticket={ticket} onClose={onClose} ticketId={ticketId} ctx_api={ctx_api} ctx_user={ctx_user} onSupportSuccess={handleSupportSuccess}/>
        )}
      </Dialog>
    </ThemeProvider>
  );
};

export default ViewDetailsModal;
